// i18n configuration constants.
//
// The supported-language list is BUILD-TIME configured via `VITE_SUPPORTED_LANG`
// (comma-separated BCP-47 language tags, e.g. "en,es-MX") so adding a language
// is an env-edit + redeploy, no code change. This list MUST stay in sync with
// the BE's `config.yaml` `i18n.supportedLanguages` — otherwise the FE will
// offer a language the BE has no notification copy / error translations for.
//
// Casing convention: canonical BCP-47 — lowercase language subtag, uppercase
// region subtag (`en`, `es-MX`, `zh-Hant-HK`). Stored as-authored here so
// downstream consumers (Intl APIs, bundle paths) see the canonical form;
// comparisons against this list are case-insensitive so `?l=es-mx` from a
// hand-typed URL still resolves.
//
// See `docs/i18n.md`.

const rawSupported = import.meta.env.VITE_SUPPORTED_LANG ?? 'en';

/**
 * Authoritative list of language tags the FE will accept from any source —
 * Profile.language, `?l=` query param, localStorage, navigator.language.
 * Any value not in this list (case-insensitive) is filtered out at the
 * detection chain layer (see `src/i18n/index.ts`), so downstream code can
 * trust `i18n.language` is always one of these (in canonical case).
 */
export const SUPPORTED_LANGUAGES: string[] = rawSupported
  .split(',')
  .map((s: string) => s.trim())
  .filter((s: string): boolean => s.length > 0);

/**
 * Fallback language used when no other signal resolves. Matches the BE's
 * `config.yaml` `i18n.defaultLanguage`.
 */
export const DEFAULT_LANGUAGE = 'en';

/**
 * Case-insensitive lookup that returns the canonical form of `code` from
 * SUPPORTED_LANGUAGES, or null on miss. Use this when you need the canonical
 * tag (e.g. before passing to Intl APIs or persisting to the profile).
 */
export function canonicalLanguage(code?: string | null): string | null {
  if (!code) return null;
  const lower = code.toLowerCase();
  return SUPPORTED_LANGUAGES.find((s) => s.toLowerCase() === lower) ?? null;
}

/**
 * True when the given code is in SUPPORTED_LANGUAGES. Case-insensitive on
 * input — callers can pass `Profile.language` or `navigator.language`
 * fragments without pre-normalizing. Returns false on null/undefined/empty
 * so consumers can guard cleanly:
 *
 *   if (isSupportedLanguage(profile.language)) { i18n.changeLanguage(profile.language) }
 */
export function isSupportedLanguage(code?: string | null): boolean {
  return canonicalLanguage(code) !== null;
}
